import React, {useState} from 'react';
import {PieChart, Pie, BarChart, Cell, ResponsiveContainer, CartesianGrid, Tooltip, XAxis, YAxis, Legend, Bar, Label, LabelList} from 'recharts';
import Layout from '../../Shared/Layout';
import {Container, } from "@material-ui/core";
import i18n, { Trans } from "../../utils/i18n";
import classNames from "classnames";
import {Switch} from "@headlessui/react";
import {usePage} from "@inertiajs/inertia-react";

function BarChartWrapper({ data, }) {
    return (
        <div className={classNames({
            'w-full mb-10': true,
            'h-[200px]': data.length <= 2,
            'h-[250px]': data.length === 3,
            'h-[350px]': data.length === 4,
            'h-[450px]': data.length === 5,
            'h-[550px]': data.length === 6,
            'h-[650px]': data.length === 7,
            'h-[750px]': data.length > 7,
        })}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    width={800}
                    height={500}
                    data={data}
                    margin={{
                        top: 5,
                        right: 30,
                        left: 100,
                        bottom: 5
                    }}
                    layout={'vertical'}
                >
                    <defs>
                        <linearGradient id="colorPv" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#FF4F50" stopOpacity={1} />
                            <stop offset="100%" stopColor="#CE0A7F" stopOpacity={1} />
                        </linearGradient>
                        <linearGradient id="colorB2X" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#ff5050" stopOpacity={1} />
                            <stop offset="100%" stopColor="#ce097f" stopOpacity={1} />
                        </linearGradient>
                        <linearGradient id="colorGreen" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#138748" stopOpacity={1} />
                            <stop offset="100%" stopColor="#59C665" stopOpacity={1} />
                        </linearGradient>
                    </defs>
                    <XAxis type={'number'} tick={false} axisLine={false}/>
                    <YAxis
                        type="category"
                        dataKey="name"
                        tick={{ width: 120 }}
                        tickSize={15}
                    />
                    <Legend verticalAlign="top" height={36}/>
                    {/* <Tooltip /> */}
                    {/*<Legend wrapperStyle={{ color: 'black' }} />*/}
                    <Bar isAnimationActive={false} dataKey="Repair" stackId="a" fill="url(#colorB2X)" radius={[10, 0, 0, 10]}>
                        <LabelList dataKey="labelRepair" position="inside" fill="#fff" style={{ fontSize: '18px' }}  />
                    </Bar>
                    <Bar isAnimationActive={false} dataKey="AvoidedEmission" stackId="a" fill="url(#colorGreen)" radius={[0, 10, 10, 0]}>
                        <LabelList dataKey="labelAvoided" position="inside" fill="#fff" style={{ fontSize: '18px' }}  />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

export function formatUnit(value, unit = '') {
    if(unit === 'kg' && value > 1000) {
        value = value / 1000;
        unit = 't';
    }
    if(unit === 'balloons' && value > 10000) {
        value = value / 1000;
        unit = 'k balloons';
    }
    value = value.toLocaleString('en-US', { maximumFractionDigits: 0, });
    return `${value} ${unit}`;
}

export default function AvoidedEmissionsEnterprise({ result, isEnterprise, }) {
    const { countries } = usePage().props;
    const entry = result.models[0];
    const device = entry.product;
    const coEmitReplacement = Math.round(result.total_device_pcf); // Math.round(entry.sourcing_and_production + entry.transport + entry.eol);
    const coEmitReplacementKg = formatUnit(Math.round(result.total_device_pcf), 'kg');
    const coEmitRepair =  Math.round(result.repair_emission );
    const coFactor =  Math.round(coEmitReplacement / coEmitRepair );
    const avoidedEmission = Math.round(result?.avoided_emission);
    const fault = entry.problem;
    const km_of_car_ride = formatUnit(result.km_of_car_ride,'km');
    const balloons = formatUnit(result.balloons, 'balloons');
    const trees_compensation = formatUnit(result.trees_compensation);
    const avoided_emission = formatUnit(result.avoided_emission, 'kg');
    const trees_text = trees_compensation == 1 ? 'tree' : 'trees';
    const [normalize, setNormalize] = useState(false);

    const groupedByCountry = result.models
        .reduce((acc, model) => {
            const country = countries.find(item => item.code === model.country)?.name || '';
            const found = acc.find(item => item.name === country);
            if(!found) {
                return [
                    ...acc,
                    {
                        name: country,
                        Repair: model.repair_emission,
                        AvoidedEmission: model.avoided_emission,
                        amount: parseInt(model.amount),
                    }
                ];
            }

            return [
                ...(acc.filter( item => item.name !== country)),
                {
                    name: country,
                    Repair: found.Repair + model.repair_emission,
                    AvoidedEmission: found.AvoidedEmission + model.avoided_emission,
                    amount: found.amount + parseInt(model.amount),
                },
            ];
        }, [])
        .map(item => ({
            ...item,
            Repair: normalize ? item.Repair / item.amount : item.Repair,
            AvoidedEmission: normalize ? item.AvoidedEmission / item.amount : item.AvoidedEmission,
            labelRepair: formatUnit(normalize ? item.Repair / item.amount : item.Repair, 'kg') + ' C02e',
            labelAvoided: formatUnit(normalize ? item.AvoidedEmission / item.amount : item.AvoidedEmission, 'kg') + ' C02e',
        }));

    const groupedByDevice = result.models
        .reduce((acc, model) => {
            const product = model.product;
            const found = acc.find(item => item.name === product);
            if(!found) {
                return [
                    ...acc,
                    {
                        name: product,
                        Repair: model.repair_emission,
                        AvoidedEmission: model.avoided_emission,
                        amount: parseInt(model.amount),
                    }
                ];
            }

            return [
                ...(acc.filter( item => item.name !== product)),
                {
                    name: product,
                    Repair: found.Repair + model.repair_emission,
                    AvoidedEmission: found.AvoidedEmission + model.avoided_emission,
                    amount: found.amount + parseInt(model.amount),
                },
            ];
        }, [])
        .map(item => ({
            ...item,
            Repair: normalize ? item.Repair / item.amount : item.Repair,
            AvoidedEmission: normalize ? item.AvoidedEmission / item.amount : item.AvoidedEmission,
            labelRepair: formatUnit(normalize ? item.Repair / item.amount : item.Repair, 'kg') + ' C02e',
            labelAvoided: formatUnit(normalize ? item.AvoidedEmission / item.amount : item.AvoidedEmission, 'kg') + ' C02e',
        }));

    const groupedByIssue = result.models
        .reduce((acc, model) => {
            const fault = model.problem.charAt(0).toUpperCase() + model.problem.slice(1);
            const found = acc.find(item => item.name === fault);
            if(!found) {
                return [
                    ...acc,
                    {
                        name: fault,
                        Repair: model.repair_emission,
                        AvoidedEmission: model.avoided_emission,
                        amount: parseInt(model.amount),
                    }
                ];
            }

            return [
                ...(acc.filter( item => item.name !== fault)),
                {
                    name: fault,
                    Repair: found.Repair + model.repair_emission,
                    AvoidedEmission: found.AvoidedEmission + model.avoided_emission,
                    amount: found.amount + parseInt(model.amount),
                },
            ];
        }, [])
        .map(item => ({
            ...item,
            Repair: normalize ? item.Repair / item.amount : item.Repair,
            AvoidedEmission: normalize ? item.AvoidedEmission / item.amount : item.AvoidedEmission,
            labelRepair: formatUnit(normalize ? item.Repair / item.amount : item.Repair, 'kg') + ' C02e',
            labelAvoided: formatUnit(normalize ? item.AvoidedEmission / item.amount : item.AvoidedEmission, 'kg') + ' C02e',
        }));

    return (
        <>
            <p className={'m-0 text-3xl font-bold text-black'}>
                <Trans i18nKey="carbon.avoidedEmissionsByLifecycleService">
                    Avoided Emissions by Lifecycle Service
                </Trans>
            </p>
            <p className={'m-0 text-lg mt-3 font-medium'}>
                <Trans i18nKey="carbon.avoidedEmissionsByLifecycleServiceDescription">
                    Below you can find a breakdown of the CO2e emissions of the specified devices and repair volumes. This snapshot of our Product Lifecycle Carbon Calculator gives you first insights on its calculation capabilities regarding accuracy and grade of detail. This ISO 14067 certified tool is based on real CO2e calculations considering actual individual phone model specifications, country energy mixes, country average transport distances and actual repair processes.
                </Trans>
                <br/>
                <Trans i18nKey="carbon.avoidedEmissionsByLifecycleServiceDescription2">
                    When it comes to repairing the specified device, emissions are generated at various stages, including transportation, the repair operation, and the production of new replacement parts. In contrast, opting to replace these devices with new ones would result in a larger carbon footprint due to the procurement of raw materials, the manufacturing process, and the distribution from the factory to the final point of use.
                </Trans>
            </p>
            <div className={'mt-8 flex justify-center'}>
                <Switch.Group as="div" className="flex items-center">
                    <Switch
                        checked={normalize}
                        onChange={setNormalize}
                        className={classNames(
                            normalize ? 'bg-secondary' : 'bg-gray-200',
                            'p-0 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2'
                        )}
                    >
                        <span
                            aria-hidden="true"
                            className={classNames(
                                normalize ? 'translate-x-5' : 'translate-x-0',
                                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                            )}
                        />
                    </Switch>
                    <Switch.Label as="span" className="ml-3 text-sm">
                        <span className="font-medium text-gray-900">
                            <Trans i18nKey="carbon.showPerDevice">
                                Average per device
                            </Trans>
                        </span>{' '}
                        <span className="text-gray-500"></span>
                    </Switch.Label>
                </Switch.Group>
            </div>
            <h2 className={'text-left '}>
                <Trans i18nKey="carbon.byCountry">
                    By Country
                </Trans>
            </h2>
            <BarChartWrapper data={groupedByCountry} />
            <h2 className={'text-left '}>
                <Trans i18nKey="carbon.byDevice">
                    By Device
                </Trans>
            </h2>
            <BarChartWrapper data={groupedByDevice} />
            <h2 className={'text-left '}>
                <Trans i18nKey="carbon.byIssue">
                    By Issue
                </Trans>
            </h2>
            <BarChartWrapper data={groupedByIssue} />
        </>
    )
}


AvoidedEmissionsEnterprise.layout = page => <Layout children={page} />;
